/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable no-restricted-syntax */

import React, {
    useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
    bool, object, shape, string,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const VideoComponent = ({
    videoUrl, contentType, classes, videoBlock, previewImage, videoClass, productBaseCode, isBRY, isMuted,
}) => {
    const videoRef = useRef();
    const dispatch = useDispatch();

    // Memoized base tracking data
    const baseTrackData = useMemo(() => ({
        eventCategory: 'product video',
        eventLabel: `${productBaseCode} | ${videoUrl}`,
        video_url: videoUrl,
        video_provider: 'internal',
        video_title: videoBlock?.video?.title || productBaseCode,
    }), [productBaseCode, videoUrl]);

    // Centralized track
    const track = useCallback((eventAction, eventData = {}) => {
        dispatch(trackEvent({
            ...baseTrackData,
            ...eventData,
            eventAction,
        }));
    }, [dispatch, baseTrackData]);

    // Progress flags in ref — persist across renders without causing re-renders
    const progressFlags = useRef({
        ten: false,
        twentyFive: false,
        fifty: false,
        seventyFive: false,
    });

    const resetProgressFlags = useCallback(() => {
        progressFlags.current = {
            ten: false,
            twentyFive: false,
            fifty: false,
            seventyFive: false,
        };
    }, []);

    // Shared progress event data builder
    const trackProgress = useCallback((label, target) => {
        track(label, {
            ga_event_name: 'video_progress',
            video_duration: target?.duration,
            video_current_time: target?.currentTime,
        });
    }, [track]);

    const onPlayEvent = useCallback((e) => {
        track('start', {
            ga_event_name: 'video_start',
            video_duration: e.target?.duration,
        });
    }, [track]);

    const onPauseEvent = useCallback((e) => {
        const { currentTime, duration } = e.target;
        if (currentTime !== duration) {
            track('pause', {
                ga_event_name: 'video_pause',
                video_duration: duration,
                video_current_time: currentTime,
            });
        }
    }, [track]);

    const onSeeking = useCallback((e) => {
        resetProgressFlags();
        if (e.target?.currentTime !== 0) {
            track('seeking', {
                ga_event_name: 'video_progress',
                video_duration: e.target?.duration,
                video_current_time: e.target?.currentTime,
            });
        }
    }, [track, resetProgressFlags]);

    const onEnded = useCallback((e) => {
        resetProgressFlags();
        track('100% progress', {
            ga_event_name: 'video_complete',
            video_duration: e.target?.duration,
            video_current_time: e.target?.currentTime,
        });

        if (videoRef.current) {
            resetProgressFlags();
            videoRef.current.currentTime = 0;
            videoRef.current.play();
        }
    }, [track, resetProgressFlags]);

    const onVolumeChange = useCallback(() => {
        if (videoRef.current) {
            track(videoRef.current.muted ? 'mute' : 'volume change');
        }
    }, [track]);

    const onWaiting = useCallback(() => {
        track('buffering');
    }, [track]);

    const onTimeUpdate = useCallback((e) => {
        const { duration, currentTime } = e.target;
        if (!duration || !currentTime) return;

        const perc = Math.floor((currentTime / duration) * 100);
        const flags = progressFlags.current;

        const milestones = [
            { threshold: 10, flagKey: 'ten', label: '10% progress' },
            { threshold: 25, flagKey: 'twentyFive', label: '25% progress' },
            { threshold: 50, flagKey: 'fifty', label: '50% progress' },
            { threshold: 75, flagKey: 'seventyFive', label: '75% progress' },
        ];

        for (const { threshold, flagKey, label } of milestones) {
            if (!flags[flagKey] && perc >= threshold) {
                progressFlags.current[flagKey] = true;
                trackProgress(label, e.target);
                break;
            }
        }
    }, [trackProgress]);

    useEffect(() => {
        const eventDomApiMap = {
            webkitfullscreenchange: 'webkitFullscreenElement',
            mozfullscreenchange: 'mozFullScreenElement',
            msfullscreenchange: 'msFullscreenElement',
            fullscreenchange: 'fullscreenElement',
        };

        const videoFullScreenTrackEvent = (event) => {
            const fullscreenApi = eventDomApiMap[event.type];
            const isFullscreen = Boolean(document[fullscreenApi]);
            track(isFullscreen ? 'fullscreen' : 'exit fullscreen');
        };

        const videoEl = videoRef.current;
        const events = Object.keys(eventDomApiMap);

        if (videoEl) {
            events.forEach((event) => videoEl.addEventListener(event, videoFullScreenTrackEvent, false));
        }

        // Cleanup on unmount
        return () => {
            if (videoEl) {
                events.forEach((event) => videoEl.removeEventListener(event, videoFullScreenTrackEvent, false));
            }
        };
    }, [track]);

    if (contentType === 'video/mp4') {
        return (
            <video
                data-testid="hero-product-video"
                id="productVideo"
                controls
                poster={previewImage}
                className={videoClass}
                onPlay={(e) => onPlayEvent(e)}
                onPause={(e) => onPauseEvent(e)}
                onSeeking={(e) => onSeeking(e)}
                onEnded={(e) => onEnded(e)}
                onVolumeChange={() => onVolumeChange()}
                onWaiting={() => onWaiting()}
                onTimeUpdate={(e) => onTimeUpdate(e)}
                autoPlay={isBRY}
                muted={isMuted}
                ref={videoRef}
                playsinline="playsinline"
            >
                <source src={videoUrl} type="video/mp4" preload="metadata" />
                <track kind="captions" />
            </video>
        );
    }

    return (
        <ResponsiveImage
            className={classes.productImage}
            path={videoUrl}
            alt="Video"
            params={{
                desktop: 'width=545&height=597',
                tablet: 'width=406&height=445&quality=100',
            }}
            breakpoints={{
                desktop: '1024px',
                tablet: '640px',
            }}
        />
    );
};

VideoComponent.propTypes = {
    classes: object.isRequired,
    videoUrl: string.isRequired,
    videoClass: string.isRequired,
    previewImage: string,
    productBaseCode: string,
    contentType: string,
    videoBlock: shape({
        video: shape({
            title: string,
        }),
    }),
    isBRY: bool,
    isMuted: bool,
};

VideoComponent.defaultProps = {
    videoBlock: {},
    previewImage: '',
    contentType: '',
    productBaseCode: '',
    isBRY: false,
    isMuted: false,
};

export default VideoComponent;
